<script>
import {defineComponent} from 'vue'
import {useDeliveryStore} from '@js/store/DeliveryStore.js'

import MobileTopBar from "@js/components/TopBar/MobileTopBar.vue";
import Breadcrumbs from "@js/components/Breadcrumbs/Breadcrumbs.vue";
import IconPaymentMaestro from "@js/components/Icons/IconPaymentMaestro.vue";
import IconPaymentVisa from "@js/components/Icons/IconPaymentVisa.vue";
import IconPaymentMir from "@js/components/Icons/IconPaymentMir.vue";
import IconPaymentMasterCard from "@js/components/Icons/IconPaymentMasterCard.vue";
import IconPaymentSbp from "@js/components/Icons/IconPaymentSbp.vue";

import DeliveryZonesMap from "@js/components/Sections/Delivery/DeliveryZonesMap.vue";
import DeliveryCalculatorPanel from "@js/components/Sections/Delivery/DeliveryCalculatorPanel.vue";
import {checkDocument} from "@js/helpers.ts";

export default defineComponent({
    name: "DeliveryAndShippingPage",
    components: {
        DeliveryCalculatorPanel,
        DeliveryZonesMap,
        IconPaymentSbp,
        IconPaymentMasterCard,
        IconPaymentMir,
        IconPaymentVisa,
        IconPaymentMaestro,
        Breadcrumbs,
        MobileTopBar,
    },

    // подключаем Pinia-store в setup, чтобы им можно было пользоваться в methods
    setup() {
        const delivery = useDeliveryStore()
        if (checkDocument()) {
            // дефолт: машина 0.5т, Зона-1
            delivery.setKind('truck')
            delivery.setVehicle('0_5t')
            delivery.setZone({id: 1, name: 'Зона 1', coeff: 1.00})
        }
        return {delivery}
    },

    data() {
        return {
            canLoadMap: false,
            selectedZone: {id: 1, name: 'Зона 1', coeff: 1.00},
            selectedPoint: null,
        };
    },

    methods: {
        onZoneChanged(z) {
            this.selectedZone = z;
            this.delivery.setZone(z); // пробрасываем в store для пересчёта цен
        },
        onPointSelected(p) {
            this.selectedPoint = p;
            this.delivery.setCoords(p.coords);
        },
    },
    mounted() {
        if (checkDocument()) {
            this.canLoadMap = true
        }
    }
})
</script>

<template>
    <MobileTopBar/>
    <div class="container">
        <Breadcrumbs/>

        <h1 class="text-2xl md:text-3xl font-semibold mb-6">
            Доставка, подъем и самовывоз в Москве и области
        </h1>

        <!-- Карта + калькулятор -->
        <section class="relative grid grid-cols-1 md:grid-cols-2 gap-5 mb-5 md:mb-10">
            <DeliveryZonesMap
                class="md:sticky md:top-24"
                v-if="canLoadMap"
                :zoom="8"
                :center="[55.460354, 37.770609]"
                @zone-changed="onZoneChanged"
                @point-selected="onPointSelected"
            />

            <!-- правая панель расчёта (читает всё из Pinia-store) -->
            <div class="flex flex-col gap-5">
                <div class="bg-white rounded-3xl p-5 md:p-6 shadow-sm border">
                    <DeliveryCalculatorPanel v-if="canLoadMap"/>
                </div>

                <!-- Правила оказания услуги доставки -->
                <div class="bg-grayBg rounded-3xl px-5 md:px-8 py-8">
                    <h2 class="text-xl md:text-2xl font-semibold mb-4">Правила оказания услуги доставки</h2>
                    <ul class="list-disc list-outside ps-5 space-y-2">
                        <li>В стандартную услугу доставки <strong>не входят</strong> разгрузка, подъем и пронос материалов — это отдельные услуги и рассчитываются дополнительно.</li>
                        <li>Пожалуйста, будьте по адресу в выбранный интервал — это помогает не сдвигать следующие рейсы. Водитель обычно звонит за <strong>10–15&nbsp;минут</strong> до прибытия.</li>
                        <li>Доставка «<strong>Точно ко времени</strong>» выполняется по указанному часу даже при недоступности телефона получателя.</li>
                        <li>Изменить время или состав заказа можно через наш контакт-центр (актуальные контакты указаны на сайте) до выхода машины на маршрут.</li>
                        <li>Если подъезд к месту разгрузки ограничен, автомобиль остановится на максимально близкой точке, не нарушая ПДД и исключая риск повреждения транспорта.</li>
                        <li>Платная городская парковка на месте выгрузки, при необходимости, <strong>оплачивается клиентом</strong>.</li>
                    </ul>
                    <p class="mt-4 text-sm text-gray-600">Информация носит справочный характер и не является публичной офертой. Уточняйте детали у менеджера при оформлении.</p>
                </div>

                <!-- Стоимость доставки: как формируется -->
                <div class="bg-grayBg rounded-3xl px-5 md:px-8 py-8">
                    <h2 class="text-xl md:text-2xl font-semibold mb-4">Стоимость доставки: как формируется</h2>
                    <p class="mb-4">Цена зависит от выбранного типа автомобиля (0,5–10&nbsp;т и 10&nbsp;т с манипулятором), расстояния от нашей базы (зоны доставки), габаритов и массы груза, а также количества адресов и точек разгрузки. Базовая стоимость в <strong>Зоне&nbsp;1</strong> умножается на коэффициент соответствующей зоны и на выбранный режим («в течение дня» или «точно ко времени»).</p>
                    <p class="mb-2">Рассчитать итоговую стоимость можно в калькуляторе на этой странице — она обновляется автоматически при выборе адреса, зоны и автомобиля.</p>
                    <ul class="list-disc list-outside ps-5 space-y-2">
                        <li>Услуги по разгрузке, проносу и подъему <strong>оплачиваются отдельно</strong>.</li>
                        <li>Для заказов в партнерские пункты выдачи могут действовать специальные тарифы — уточняйте у менеджера.</li>
                    </ul>
                </div>

                <!-- Ограничения по габаритам и длине -->
                <div class="bg-grayBg rounded-3xl px-5 md:px-8 py-8">
                    <h2 class="text-xl md:text-2xl font-semibold mb-4">Ограничения по габаритам и длине материалов</h2>
                    <p class="mb-4">Для безопасной перевозки металлопроката соблюдаем предельную длину в зависимости от тоннажа автомобиля:</p>
                    <div class="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                        <div class="bg-white rounded-xl p-3 border"><div class="font-medium">0,5&nbsp;т / 0,7&nbsp;т</div><div class="text-gray-600">до&nbsp;1,5&nbsp;м</div></div>
                        <div class="bg-white rounded-xl p-3 border"><div class="font-medium">1,5–3,6&nbsp;т</div><div class="text-gray-600">до&nbsp;4&nbsp;м</div></div>
                        <div class="bg-white rounded-xl p-3 border"><div class="font-medium">5–10&nbsp;т</div><div class="text-gray-600">до&nbsp;6&nbsp;м</div></div>
                    </div>
                    <p class="mt-4 text-sm text-gray-600">Если длина или габариты выходят за рамки стандартных ограничений, подберём индивидуальное решение — сообщите параметры менеджеру.</p>
                </div>

                <!-- Получение заказа и документы -->
                <div class="bg-grayBg rounded-3xl px-5 md:px-8 py-8">
                    <h2 class="text-xl md:text-2xl font-semibold mb-4">Получение заказа и документы</h2>
                    <ul class="list-disc list-outside ps-5 space-y-2">
                        <li><strong>Юридическим лицам</strong> при безналичной оплате может потребоваться доверенность/печать — уточните комплект документов заранее.</li>
                        <li>При доплате на адресе кассовый чек отправим на e-mail, указанный в заказе (или выдадим в офисе/при следующей доставке).</li>
                        <li>При оплате на адресе попросим спуститься к автомобилю для сверки номенклатуры и оплаты до начала разгрузки.</li>
                        <li>Вопросы по количеству/качеству товара можно задать водителю или в службу поддержки — контакты размещены на сайте.</li>
                    </ul>
                </div>

                <!-- Временные интервалы доставки -->
                <div class="bg-grayBg rounded-3xl px-5 md:px-8 py-8">
                    <h2 class="text-xl md:text-2xl font-semibold mb-4">Временные интервалы доставки</h2>
                    <ul class="list-disc list-outside ps-5 space-y-2">
                        <li>Доставляем в выбранный интервал (например, 10–18 / 12–20 / 14–22), «в течение дня» или <strong>точно ко времени</strong>.</li>
                        <li>Время на подъем/пронос <strong>не входит</strong> в интервал доставки.</li>
                        <li>При нескольких доставках на один адрес интервалы могут сдвигаться на ~1&nbsp;час.</li>
                        <li>Сложные заказы (например, окраски/колеровки больших объёмов) могут потребовать дополнительного времени на комплектацию.</li>
                        <li>Для удалённых зон возможна корректировка интервала — об этом предупредит менеджер.</li>
                        <li>В случае нашей задержки — предусмотрим компенсацию по внутренним правилам сервиса.</li>
                    </ul>
                </div>

                <!-- Простой и парковка -->
                <div class="bg-grayBg rounded-3xl px-5 md:px-8 py-8">
                    <h2 class="text-xl md:text-2xl font-semibold mb-4">Простой автомобиля и парковка</h2>
                    <ul class="list-disc list-outside ps-5 space-y-2">
                        <li>В стоимость доставки включено: до <strong>30 минут</strong> простоя для авто до 1,5&nbsp;т и до <strong>60 минут</strong> — для авто свыше 1,5&nbsp;т.</li>
                        <li>Далее — почасовая оплата простоя: ориентировочно <strong>600&nbsp;₽/час</strong> для авто до 1,5&nbsp;т и <strong>1200&nbsp;₽/час</strong> для авто свыше 1,5&nbsp;т.</li>
                        <li>Городская платная парковка на месте выгрузки оплачивается получателем самостоятельно.</li>
                    </ul>
                    <p class="mt-4 text-sm text-gray-600">Фактические тарифы зависят от категории автомобиля и маршрута — итоговую сумму уточняйте у менеджера при подтверждении заказа.</p>
                </div>

                <!-- Разгрузка манипулятором -->
                <div class="bg-grayBg rounded-3xl px-5 md:px-8 py-8">
                    <h2 class="text-xl md:text-2xl font-semibold mb-4">Разгрузка манипулятором</h2>
                    <ul class="list-disc list-outside ps-5 space-y-2">
                        <li>Манипулятор выполняет <strong>только разгрузку</strong> в зоне досягаемости стрелы и при прямой видимости точки выгрузки.</li>
                        <li>Разгрузка <strong>запрещена</strong> под линиями электропередач и над хозяйственными постройками/заборами.</li>
                        <li>Рабочая дистанция — ориентировочно <strong>2–15&nbsp;м</strong> от борта, точные параметры зависят от модели.</li>
                        <li>Доставка в подземные паркинги выполняется на машинах с открытыми бортами (возможны отличия в тарифе относительно тентованных авто).</li>
                    </ul>
                    <p class="mt-4 text-sm text-gray-600">Технические ограничения и безопасность — приоритет. Если на адресе есть особые условия, сообщите заранее.</p>
                </div>

            </div>
        </section>
    </div>
</template>

<style scoped>
/* при необходимости — доп. стили */
</style>
